import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import useLocalStorage from 'use-local-storage';
import { auth } from '../firebase';

const WorkspaceContext = createContext();

export const useWorkspace = () => useContext(WorkspaceContext);

export const WorkspaceProvider = ({ children }) => {
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeWorkspace, setActiveWorkspace] = useLocalStorage('activeWorkspace', '');
    const [idToken, setIdToken] = useState(null);
    const [user, setUser] = useState(null);
    const [firestoreUser, setFirestoreUser] = useState(null);
    const [isWorkspaceChangeLocked, setIsWorkspaceChangeLocked] = useState(false);
    // State for the confirmation modal
    const [confirmModalState, setConfirmModalState] = useState({ 
        isOpen: false, 
        workspaceToChange: null, 
        title: '', 
        message: '' 
    });
    
    // Refs to track current values for interceptors
    const currentUserRef = useRef(null);
    const idTokenRef = useRef(null);
    const isRefreshingRef = useRef(false);

    // Function to refresh the Firebase ID token
    const refreshToken = useCallback(async () => {
        if (!currentUserRef.current || isRefreshingRef.current) {
            return null;
        }

        try {
            isRefreshingRef.current = true;
            const freshToken = await currentUserRef.current.getIdToken(true); // Force refresh
            setIdToken(freshToken);
            idTokenRef.current = freshToken;
            return freshToken;
        } catch (error) {
            console.error("Error refreshing token:", error);
            return null;
        } finally {
            isRefreshingRef.current = false;
        }
    }, []);

    // Update refs when values change
    useEffect(() => {
        currentUserRef.current = user;
        idTokenRef.current = idToken;
    }, [user, idToken]);

    // Set up axios interceptor for automatic token refresh
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    const freshToken = await refreshToken();
                    if (freshToken) {
                        originalRequest.headers.Authorization = `Bearer ${freshToken}`;
                        return axios(originalRequest);
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, [refreshToken]);

    const fetchWorkspaces = useCallback(async () => {
        if (!idToken) return;
        
        try {
            setLoading(true);
            const res = await axios.get(process.env.REACT_APP_BACKEND_API_URL + '/api/auth/user/workspaces', {
                headers: { Authorization: `Bearer ${idToken}` },
            });
            setWorkspaces(res.data);
            setError(null);
        } catch (err) {
            console.error("Error fetching workspaces:", err.response ? err.response.data : err.message);
            setError('Failed to load workspaces');
        } finally {
            setLoading(false);
        }
    }, [idToken]);

    // Updated changeActiveWorkspace to trigger modal if locked
    const changeActiveWorkspace = useCallback((workspace) => {
        if (isWorkspaceChangeLocked) {
            setConfirmModalState({
                isOpen: true,
                workspaceToChange: workspace,
                title: 'Unsaved Changes',
                message: 'Changing workspace will discard unsaved changes in your current upload. Do you want to continue and refresh the page?'
            });
        } else {
            setActiveWorkspace(workspace); // Change directly if not locked
        }
    }, [isWorkspaceChangeLocked, setActiveWorkspace]);

    const fetchFirestoreUser = useCallback(async () => {
        if (!idToken) {
            return;
        }

        try {
            const res = await axios.get(process.env.REACT_APP_BACKEND_API_URL + '/api/auth/user/getUser', {
                headers: { Authorization: `Bearer ${idToken}` },
            });

            if (res.data.docExists) {
                setFirestoreUser(res.data.user);
            } else {
                setFirestoreUser(null);
            }
        } catch (err) {
            console.error("Error fetching Firestore user:", err.response ? err.response.data : err.message);
            setFirestoreUser(null);
        }
    }, [idToken]);

    // Optimize the Firebase auth listener to be more efficient
    const handleAuthStateChanged = useCallback(async (currentUser) => {
        if (currentUser) {
            try {
                const token = await currentUser.getIdToken();
                setIdToken(token);
                setUser(currentUser);
            } catch (error) {
                console.error("Error getting token in WorkspaceContext:", error);
                setIdToken(null);
                setUser(null);
            }
        } else {
            setIdToken(null);
            setUser(null);
            setActiveWorkspace('');
            setWorkspaces([]);
        }
    }, [setActiveWorkspace]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(handleAuthStateChanged);
        return () => unsubscribe();
    }, [handleAuthStateChanged]);

    useEffect(() => {
        if (idToken) {
            fetchWorkspaces();
            fetchFirestoreUser();
        }
    }, [idToken, fetchWorkspaces, fetchFirestoreUser]);

    useEffect(() => {
        if (!workspaces.some(ws => ws.id === activeWorkspace?.id) && workspaces.length > 0) {
            setActiveWorkspace(workspaces[0]);
        }
    }, [workspaces, activeWorkspace, setActiveWorkspace]);

    // Periodic token refresh to prevent expiration
    useEffect(() => {
        if (!user) return;

        const refreshInterval = setInterval(() => {
            refreshToken();
        }, 45 * 60 * 1000); // Refresh every 45 minutes

        return () => clearInterval(refreshInterval);
    }, [user, refreshToken]);

    // New functions to control the lock
    const lockWorkspaceChange = useCallback(() => {
        setIsWorkspaceChangeLocked(true);
    }, []);

    const unlockWorkspaceChange = useCallback(() => {
        setIsWorkspaceChangeLocked(false);
    }, []);

    // Function to handle confirmation from modal
    const confirmWorkspaceChange = useCallback(() => {
        if (confirmModalState.workspaceToChange) {
            setActiveWorkspace(confirmModalState.workspaceToChange);
            setConfirmModalState({ isOpen: false, workspaceToChange: null, title: '', message: '' });
            window.location.reload(); // Reload the page
        }
    }, [confirmModalState.workspaceToChange, setActiveWorkspace]);

    // Function to handle cancellation from modal
    const cancelWorkspaceChange = useCallback(() => {
        setConfirmModalState({ isOpen: false, workspaceToChange: null, title: '', message: '' });
    }, []);

    return (
        <WorkspaceContext.Provider value={{ 
            workspaces, 
            loading, 
            error, 
            activeWorkspace, 
            fetchWorkspaces, 
            changeActiveWorkspace, 
            idToken, 
            user, 
            firestoreUser, 
            lockWorkspaceChange,
            unlockWorkspaceChange,
            refreshToken,
            // Expose modal state and handlers
            confirmModalState,
            confirmWorkspaceChange,
            cancelWorkspaceChange
        }}>
            {children}
        </WorkspaceContext.Provider>
    );
};